import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api, fmtDate, scopeQuery, useScope } from "../api";
import { useT } from "../i18n";
import { useMe } from "./AdminShell";
import { Msg, useFlash } from "../ui";

/**
 * Follow Jesus · Conversations (FR-DEC-01..06): the people who said yes, their talk slot, status,
 * pastor notes (never reach the app), traffic light, baptism follow-up and the PDF sent after the talk.
 */

interface Decision {
  id: string;
  userId: string;
  name: string;
  alias: string;
  email: string | null;
  calendlyEmail: string | null;
  saidYesAt: string;
  yesEmailSentAt: string | null;
  trafficLight: "none" | "green" | "yellow" | "grey";
  baptism: { invitedAt: string | null; reminders: number; signedUpAt: string | null; callTaskAt: string | null };
  talk: { id: string | null; phone: string | null; slotAt: string | null; status: "open" | "held"; notes: string | null; pdfSentAt: string | null; viaCalendly: boolean };
}
interface Pdf {
  id: string;
  fileName: string;
  sizeBytes: number;
  pages: number | null;
  uploadedAt: string;
}
interface Data {
  decisions: Decision[];
  pdf: Pdf | null;
  calendlyUrl: string | null;
}

const MAX_PDF = 10 * 1024 * 1024;

export function AdminDecisions() {
  const { t, locale } = useT();
  const me = useMe();
  const scope = useScope();
  const qc = useQueryClient();
  const sq = scopeQuery(scope, "church");
  const [flash, run, busy] = useFlash();
  const q = useQuery({ queryKey: ["admin", "decisions", scope.churchId], queryFn: () => api<Data>(`/admin/decisions${sq}`), enabled: !(scope.canPickScope && !scope.churchId) });
  const invalidate = () => Promise.all([qc.invalidateQueries({ queryKey: ["admin", "decisions"] }), qc.invalidateQueries({ queryKey: ["admin", "attention"] }), qc.invalidateQueries({ queryKey: ["admin", "today"] })]);
  const patch = useMutation({ mutationFn: (p: { id: string; body: Record<string, unknown> }) => api(`/admin/decisions/${p.id}${sq}`, { method: "PATCH", json: p.body }), onSuccess: invalidate });
  const callDone = useMutation({ mutationFn: (id: string) => api(`/admin/decisions/${id}/call-done${sq}`, { method: "POST" }), onSuccess: invalidate });
  const sendPdf = (id: string) => run(() => api(`/admin/decisions/${id}/send-pdf${sq}`, { method: "POST" }), t("admin.decisions.pdfSent")).then(invalidate);
  const isAdmin = Boolean(me.data?.isSuperadmin || me.data?.membership?.role === "admin");
  const fileRef = useRef<HTMLInputElement>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const d = q.data;

  if (scope.canPickScope && !scope.churchId)
    return (
      <>
        <h1>{t("admin.decisions.title")}</h1>
        <p className="muted">{t("admin.fams.noChurch")}</p>
      </>
    );

  const upload = async (file: File) => {
    if (file.size > MAX_PDF) {
      await run(async () => {
        throw new Error(t("admin.decisions.pdfTooBig"));
      });
      return;
    }
    const buf = new Uint8Array(await file.arrayBuffer());
    let bin = "";
    for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
    await run(() => api(`/admin/decisions/pdf${sq}`, { method: "POST", json: { fileName: file.name, data: btoa(bin) } }), t("admin.decisions.pdfUploaded"));
    await invalidate();
  };
  const fmtSize = (n: number) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);
  const toLocal = (iso: string | null) => (iso ? new Date(new Date(iso).getTime() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 16) : "");

  return (
    <>
      <h1>{t("admin.decisions.title")}</h1>
      <p className="muted">{t("admin.decisions.body")}</p>
      {d && !d.calendlyUrl ? (
        <p className="error">
          {t("admin.decisions.noCalendly")} <Link to="/admin/settings">→</Link>
        </p>
      ) : null}
      <Msg msg={flash} />

      <div className="table-wrap">
        <table className="people">
          <thead>
            <tr>
              <th>{t("admin.decisions.col.name")}</th>
              <th>{t("admin.decisions.col.phone")}</th>
              <th>{t("admin.decisions.col.slot")}</th>
              <th>{t("admin.decisions.col.status")}</th>
              <th>{t("admin.decisions.col.light")}</th>
              <th>{t("admin.decisions.col.baptism")}</th>
              <th>{t("admin.decisions.notes")}</th>
              <th>{t("admin.decisions.pdfCard")}</th>
            </tr>
          </thead>
          <tbody>
            {d?.decisions.length === 0 ? (
              <tr>
                <td colSpan={8} className="muted">
                  {t("admin.decisions.empty")}
                </td>
              </tr>
            ) : null}
            {d?.decisions.map((x) => (
              <tr key={x.id}>
                <td>
                  <strong>{x.name}</strong>
                  <div className="muted small">{fmtDate(x.saidYesAt, locale)}{x.yesEmailSentAt ? ` · ${t("admin.decisions.yesMail")}` : ""}</div>
                  {x.email ? <div className="muted small">{x.email}</div> : null}
                </td>
                <td>
                  {x.talk.phone ? (
                    <a className="btn small ghost" href={`tel:${x.talk.phone}`}>
                      {t("admin.decisions.call")} {x.talk.phone}
                    </a>
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  <input type="datetime-local" value={toLocal(x.talk.slotAt)} onChange={(e) => patch.mutate({ id: x.id, body: { slotAt: e.target.value ? new Date(e.target.value).toISOString() : null } })} />
                  <div className="muted small">{x.talk.slotAt ? (x.talk.viaCalendly ? t("admin.decisions.viaCalendly") : t("admin.decisions.slotManual")) : t("admin.decisions.noSlot")}</div>
                </td>
                <td>
                  <select value={x.talk.status} onChange={(e) => patch.mutate({ id: x.id, body: { status: e.target.value } })}>
                    <option value="open">{t("admin.decisions.open")}</option>
                    <option value="held">{t("admin.decisions.held")}</option>
                  </select>
                </td>
                <td>
                  <select value={x.trafficLight} className={`light ${x.trafficLight}`} onChange={(e) => patch.mutate({ id: x.id, body: { trafficLight: e.target.value } })}>
                    {(["none", "green", "yellow", "grey"] as const).map((l) => (
                      <option key={l} value={l}>
                        {t(`admin.decisions.light.${l}`)}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  {x.baptism.signedUpAt ? (
                    <span className="pill ok">{t("admin.decisions.baptismSignedUp", { date: fmtDate(x.baptism.signedUpAt, locale) })}</span>
                  ) : x.baptism.invitedAt ? (
                    <>
                      <div className="small">{t("admin.decisions.baptismInvited", { date: fmtDate(x.baptism.invitedAt, locale) })}</div>
                      <div className="muted small">{t("admin.decisions.baptismReminders", { n: x.baptism.reminders })}</div>
                      {x.baptism.callTaskAt ? (
                        <button className="btn small" onClick={() => callDone.mutate(x.id)} title={t("admin.decisions.callTask")}>
                          {t("admin.decisions.callDone")}
                        </button>
                      ) : null}
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  <textarea rows={2} value={notes[x.id] ?? x.talk.notes ?? ""} onChange={(e) => setNotes({ ...notes, [x.id]: e.target.value })} />
                  {notes[x.id] !== undefined && notes[x.id] !== (x.talk.notes ?? "") ? (
                    <button className="btn small" onClick={() => patch.mutate({ id: x.id, body: { notes: notes[x.id] } })}>
                      {t("admin.decisions.saveNotes")}
                    </button>
                  ) : null}
                </td>
                <td className="nowrap">
                  {x.talk.pdfSentAt ? (
                    <span className="pill ok">
                      {t("admin.decisions.pdfSent")} · {fmtDate(x.talk.pdfSentAt, locale)}
                    </span>
                  ) : x.talk.status !== "held" ? (
                    <span className="muted small">{t("admin.decisions.pdfNeedsHeld")}</span>
                  ) : !x.email ? (
                    <span className="muted small">{t("admin.decisions.pdfNoEmail")}</span>
                  ) : (
                    <button className="btn small" disabled={busy || !d?.pdf} onClick={() => void sendPdf(x.id)} title={x.calendlyEmail ? t("admin.decisions.pdfMeta", { email: x.calendlyEmail }) : x.email}>
                      {t("admin.decisions.sendPdf")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <h2>{t("admin.decisions.pdfCard")}</h2>
        {d?.pdf ? (
          <p>
            <strong>{d.pdf.fileName}</strong> <span className="muted">{t("admin.decisions.pdfInfo", { date: fmtDate(d.pdf.uploadedAt, locale), size: fmtSize(d.pdf.sizeBytes), pages: d.pdf.pages ?? "?" })}</span>
          </p>
        ) : (
          <p className="muted">{t("admin.decisions.pdfNone")}</p>
        )}
        {isAdmin ? (
          <>
            <input ref={fileRef} type="file" accept="application/pdf" style={{ display: "none" }} onChange={(e) => e.target.files?.[0] && void upload(e.target.files[0])} />
            <button className="btn ghost" disabled={busy} onClick={() => fileRef.current?.click()}>
              {t("admin.decisions.pdfUpload")}
            </button>
          </>
        ) : null}
      </div>
    </>
  );
}
